import { ShopigurusHTMLElement } from '@shopigurus/base';

/**
 * ShpfltCartUpsell — Cart-drawer upsell carousel.
 *
 * Handles the variant <select>, the ADD button (AJAX add-to-cart that re-renders
 * the cart drawer) and an accessible autoplay carousel with progress dots.
 * Self-initialises on every render: the cart drawer recreates this element each
 * time its contents are re-rendered, so connectedCallback re-runs automatically.
 *
 * @example
 * <shpflt-cart-upsell class="shpflt-cart-upsell">…</shpflt-cart-upsell>
 */
class ShpfltCartUpsell extends ShopigurusHTMLElement {
  /** @type {number} */
  #index = 0;
  /** @type {number|null} */
  #timer = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.abortController;
    this.addEventListener('change', this.#onVariantChange, { signal });
    this.addEventListener('click', this.#onAddClick, { signal });
    this.#startAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopAutoplay();
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return Array.from(this.querySelectorAll('.shpflt-cart-upsell__slide'));
  }

  /** @returns {HTMLElement[]} */
  get #dots() {
    return Array.from(this.querySelectorAll('.shpflt-cart-upsell__dot'));
  }

  #startAutoplay() {
    this.#stopAutoplay();
    const slides = this.#slides;
    if (slides.length < 2) return;

    this.#index = 0;
    this.#setActiveDot(0);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#timer = window.setInterval(() => {
      this.#index = (this.#index + 1) % slides.length;
      const target = slides[this.#index];
      if (!target) return;
      this.refs.list?.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      this.#setActiveDot(this.#index);
    }, 5000);
  }

  #stopAutoplay() {
    if (!this.#timer) return;
    window.clearInterval(this.#timer);
    this.#timer = null;
  }

  /**
   * Marks dots before the active index as complete and animates the active one.
   * @param {number} activeIndex
   */
  #setActiveDot(activeIndex) {
    const dots = this.#dots;
    if (!dots.length) return;

    dots.forEach((dot, i) => {
      dot.classList.remove('is-active', 'is-complete');
      if (i < activeIndex) dot.classList.add('is-complete');
    });

    const active = dots[activeIndex];
    if (!active) return;
    // Double rAF so the fill restarts its width transition from 0.
    requestAnimationFrame(() => requestAnimationFrame(() => active.classList.add('is-active')));
  }

  /**
   * Syncs the ADD button with the selected variant.
   * @param {Event} event
   */
  #onVariantChange = (event) => {
    const select = event.target.closest('.shpflt-cart-upsell-item__select');
    if (!select) return;

    const button = select
      .closest('.shpflt-cart-upsell-item__actions')
      ?.querySelector('.shpflt-cart-upsell-item__add');
    if (!button) return;

    button.dataset.variantId = select.value;
    button.disabled = !select.value;
  };

  /**
   * @param {Event} event
   */
  #onAddClick = (event) => {
    const button = event.target.closest('.shpflt-cart-upsell-item__add');
    if (!button) return;
    event.preventDefault();

    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    this.#addToCart(button, variantId);
  };

  /**
   * Adds the variant to the cart and re-renders the cart drawer.
   * @param {HTMLButtonElement} button
   * @param {string} variantId
   * @returns {Promise<void>}
   */
  async #addToCart(button, variantId) {
    const cartDrawer = document.querySelector('cart-drawer');

    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');

    try {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: cartDrawer
          ? cartDrawer.getSectionsToRender().map((section) => section.id)
          : ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();

      if (data.status) {
        console.error(data.description || 'Error adding upsell item to cart');
        return;
      }

      if (cartDrawer) {
        // renderContents only swaps inner HTML, so the <cart-drawer> wrapper keeps
        // its is-empty class when adding the first item from an empty cart.
        cartDrawer.classList.toggle('is-empty', data.item_count === 0);
        cartDrawer.renderContents(data);
      }
    } catch (error) {
      console.error('Upsell add to cart failed', error);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    }
  }
}

customElements.define('shpflt-cart-upsell', ShpfltCartUpsell);
